import React from 'react';
import './styles.css';

// The one navigation model inside a project: Idea → Edit → Post → Report.
// "Edit" is the user-facing name for the backend "workspace" step.
const STEPS = [
    { key: 'idea', label: 'Idea' },
    { key: 'workspace', label: 'Edit' },
    { key: 'post', label: 'Post' },
    { key: 'report', label: 'Report' },
];

const STEP_ORDER = STEPS.map((s) => s.key);

// Backend still emits "analysis" as a transitional step; fold it into Post so
// the stepper only ever shows the four user-facing steps.
const normalizeStep = (step) => (step === 'analysis' ? 'post' : step);

// What a frame says under its title when the caller has nothing better.
const DEFAULT_NOTES = {
    done: 'Done',
    current: 'Working on it',
    upcoming: 'Up next',
    skipped: 'Skipped for a quick edit',
};

/**
 * The project as a storyboard strip.
 *
 * Four numbered frames read left to right, the way a board is read: each
 * carries its step and a one-line note, the frame in progress is outlined in
 * red marker, finished frames are filled, and a skipped frame is struck
 * through. State is drawn as a mark on the frame rather than a colour wash,
 * so it reads the same at a glance and to a screen reader.
 *
 * `notes` lets the page say something specific about a frame ("2:14 cut",
 * "Scheduled Fri 6pm"); `action` is the one next step, set at the strip's
 * right end where the eye lands after reading the frames.
 */
function ProjectStepper({ currentStep, flowType = 'full', finalizedSteps = {}, onStepClick, notes = {}, action = null, verdict = '' }) {
    const current = normalizeStep(currentStep || 'idea');
    const currentIndex = STEP_ORDER.indexOf(current);
    const isQuickEdit = flowType === 'quick-edit';

    const statusFor = (key, index) => {
        if (isQuickEdit && key === 'idea') {
            return 'skipped';
        }
        if (index === currentIndex) {
            return 'current';
        }
        if ((finalizedSteps && finalizedSteps[key]) || index < currentIndex) {
            return 'done';
        }
        return 'upcoming';
    };

    return (
        <div className="storyboard-strip">
            <ol className="storyboard-frames" aria-label="Project steps">
                {STEPS.map((step, index) => {
                    const status = statusFor(step.key, index);
                    const clickable = Boolean(onStepClick) && status !== 'skipped' && status !== 'current';
                    // The report frame carries the verdict in red marker once there is one:
                    // the line the next board starts from.
                    const isVerdict = step.key === 'report' && Boolean(verdict);
                    const note = isVerdict ? verdict : (notes[step.key] || DEFAULT_NOTES[status]);
                    return (
                        <li key={step.key} className={`storyboard-frame is-${status}`}>
                            <button
                                type="button"
                                className="storyboard-frame-button"
                                aria-current={status === 'current' ? 'step' : undefined}
                                disabled={!clickable}
                                onClick={clickable ? () => onStepClick(step.key) : undefined}
                            >
                                <span className="storyboard-frame-number sb-frame-label" aria-hidden="true">
                                    {index + 1}
                                </span>
                                <span className="storyboard-frame-title">{step.label}</span>
                                <span className={`storyboard-frame-note${isVerdict ? ' is-verdict' : ''}`} title={isVerdict ? verdict : undefined}>{note}</span>
                            </button>
                        </li>
                    );
                })}
            </ol>
            {action && <div className="storyboard-action">{action}</div>}
        </div>
    );
}

export { ProjectStepper };
